<template>
    <div>
        <div :class="['predictor', 'page', 'page--light', 'page--hide-top-nav-on-mob', {'predictor--partner-app': isPartnerApp}]">
            <back-button class="back-btn" />
            <section class="banner"
                :style="{ backgroundImage: `url(${partner.partnerBanner})` }"
            >
                <partner-logo class="logo"
                    :show-placeholder="!partner.partnerLogo"
                    :url="partner.partnerLogo"
                />

                <header>
                    <h2 class="banner__partner-name">{{ partner.name }}</h2>
                </header>

                <loading-spinner v-if="partner.partnerBanner"
                    class="banner__loader"
                />
            </section>

            <div class="center-wrapper">
                <loading-spinner v-if="noPartnerOrSportEvent"
                    class="no-prediction-spinner"
                />
                <section v-else
                    class="prediction-section"
                >
                    <div v-if="sportEvent"
                        class="prediction-carousel"
                    >
                        <div class="prediction center">
                            <div class="prediction__status">
                                <status v-if="status"
                                    :status="status"
                                />
                            </div>

                            <span v-if="!hasBalanceToVote && !userVotingEnded"
                                class="prediction__no-balance"
                            >
                                {{ `${$t('predictors.details.fanTokenNeeded', { name: currencyShortCode })}` }}
                            </span>
                            <h4 class="prediction__event-name">{{ sportEvent.name }}</h4>
                            <span class="prediction__match-time">{{ matchTime }}</span>
                            <span class="prediction__season-name">{{ sportEvent.season.name }}</span>

                            <div class="prediction__versus">
                                <div class="prediction__versus__team">
                                    <team-button
                                        class="prediction__versus__logo"
                                        :src="homeJersey"
                                        :isSelected="userChosenTeamQualifier === 'home'"
                                        :userHasParticipated="false"
                                        :userCanParticipate="false"
                                        :alternativeColor="alternativeCrownLogo"
                                        type="predict"
                                    />
                                    <span class="prediction__versus__team-name">
                                        {{ homeTeamName }}
                                    </span>
                                </div>

                                <div class="prediction__versus__wrapper">
                                    <prediction-outcome v-if="showPredictionOutcome" />
                                    <div class="prediction__versus__dropdowns"
                                        @click="openSelectorModal()"
                                    >
                                        <score-selector
                                            :disabled="isInputDisabled"
                                            :value="userChosenAnswer"
                                            :isResultDefined="showPredictionOutcome"
                                            :isCorrectDifference="outcomeCorrectScoreRange"
                                            :userHasVoted="userSubmittedAnswer && userSubmittedAnswer.length > 1"
                                        />
                                    </div>
                                </div>
                                <div class="prediction__versus__team">
                                    <team-button
                                        class="prediction__versus__logo"
                                        :src="awayJersey"
                                        :isSelected="userChosenTeamQualifier === 'away'"
                                        :userHasParticipated="false"
                                        :userCanParticipate="false"
                                        :alternativeColor="alternativeCrownLogo"
                                        type="predict"
                                    />
                                    <span class="prediction__versus__team-name">
                                        {{ awayTeamName }}
                                    </span>
                                </div>
                            </div>
                            <xp-points v-if="showPredictionOutcome"
                                :teamId="teamId"
                            />
                            <span v-if="showStatistics"
                                class="prediction__vote-count"
                            >
                                {{ numberOfVotesCast }}
                            </span>
                        </div>
                    </div>
                </section>

                <!-- current vote distribution -->
                <section v-if="showStatistics"
                    class="popular center"
                >
                    <h4 class="popular__title">
                        {{ $t('predictors.details.popularPredictions') }}
                    </h4>
                    <popular-predictions
                        :teamId="teamId"
                        :homeTeamId="homeTeam.id"
                        :awayTeamId="awayTeam.id"
                        :homeLogo="homeJersey"
                        :awayLogo="awayJersey"
                        :homeTeamName="homeTeam.name"
                        :awayTeamName="awayTeam.name"
                    />
                </section>

                <section class="about center">
                    <h4 class="about__title">
                        {{ $t('predictors.details.aboutTitle') }}
                    </h4>
                    <p class="about__description well well--no-margin">
                        {{ $t('predictors.americanFootball.details.aboutWeb') }}
                    </p>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
// 3rd party
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Utils
import {
    format,
} from '@shared/utils/dates.js';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';
import partnerLogo from '@webapp/components/partner-logo/partner-logo.vue';
import status from '@webapp/modules/fan-engagement/components/predictor/status.vue';
import predictionOutcome from '@webapp/modules/fan-engagement/components/predictor/american-football/outcome.vue';
import xpPoints from '@webapp/modules/fan-engagement/components/predictor/american-football/xp-points.vue';
import popularPredictions from '@webapp/modules/fan-engagement/components/predictor/american-football/popular-predictions.vue';
import scoreSelector from '@webapp/modules/fan-engagement/components/predictor/american-football/score-selector.vue';
import teamButton from '@webapp/modules/fan-engagement/components/predictor/team-button.vue';

import {
    AMERICAN_FOOTBALL_SCORE_RANGES,
} from '@shared/modules/fan-engagement/constants/predictableSportEvents';

// Local Types
import localEpicTypes from '@webapp/store/types.js';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';

// Assets
import successIcon from '@webapp/assets/statuses/success.webp';
import errorIcon from '@webapp/assets/oopps.webp';

// Socios variables
import socios from '@webapp/socios.json';

export default {
    name: 'predictor-details-v2-american-football',
    components: {
        backButton,
        loadingSpinner,
        partnerLogo,
        status,
        predictionOutcome,
        popularPredictions,
        scoreSelector,
        xpPoints,
        teamButton,
    },
    data() {
        return {
            isSubmitting: false,
        };
    },
    computed: {
        ...mapGetters([
            'getLatestValidationError',
            'isPartnerApp',
            'isUsaRegulation',
        ]),

        ...mapGetters('wallet', [
            'wallet',
            'decentralizedLedgerByToken',
        ]),

        ...mapGetters('partners', ['partnerById']),

        ...mapGetters('predictorSportEvents', [
            'sportEvent',
            'isSportEventResultDefined',
            'homeLogo',
            'awayLogo',
            'getSubmittedPredictions',
            'getTeamAllowPredictionIfNotFan',
            'homeTeam',
            'awayTeam',
            'getSportEventTeamJerseyByQualifier',
        ]),

        ...mapGetters('predictorSports', ['sportIds', 'getSportDefaultJersey']),

        ...mapGetters('predictorPredictableSportEvents', [
            'getScoreRangePredictionForTeamId',
            'getWinningTeamPredictionForTeamId',
            'hasUserExistingScoreRangeVotingEnded',
            'getUserScoreRangePredictionAnswer',
            'getUserWinningTeamPredictionAnswer',
            'getOutcomeCorrectWinningTeamAmericanFootball',
            'getUserSelectedIndex',
            'getUserSelectedTeamQualifier',
            'getOutcomeCorrectScoreRangeAmericanFootball',
        ]),

        ...mapGetters('voteTokens', ['getVoteTokenByCurrencyId']),

        ...mapGetters('predictorTeams', [
            'getTeamPartnerIdById',
            'getCurrentTeam',
        ]),

        defaultSportJersey() {
            return (this.getSportDefaultJersey(this.sportEvent?.sportTypeId));
        },

        homeJersey() {
            return this.getSportEventTeamJerseyByQualifier(this.sportEventId, this.partnerId, false, 'home') || this.defaultSportJersey;
        },

        awayJersey() {
            return this.getSportEventTeamJerseyByQualifier(this.sportEventId, this.partnerId, false, 'away') || this.defaultSportJersey;
        },

        homeTeamName() {
            return this.homeTeam.name;
        },

        awayTeamName() {
            return this.awayTeam.name;
        },

        scoreRangePrediction() {
            return this.getScoreRangePredictionForTeamId(this.teamId);
        },

        winningTeamPrediction() {
            return this.getWinningTeamPredictionForTeamId(this.teamId);
        },

        teamId() {
            return this.$route.params.teamId;
        },

        noPartnerOrSportEvent() {
            return !this.sportEvent ||
                !this.partner ||
                (this.sportEvent && this.sportEvent.id !== this.sportEventId) ||
                this.teamId !== this.getCurrentTeam?.id;
        },

        partnerId() {
            return this.getTeamPartnerIdById(this.$route.params.teamId);
        },

        sportEventId() {
            return this.$route.params.sportEventId;
        },

        numberOfVotesCast() {
            if (!this.scoreRangePrediction || !this.winningTeamPrediction) {
                return '';
            }

            return `${this.scoreRangePrediction.voteCount} ${this.$t('predictors.details.predictions')}`;
        },

        partner() {
            return this.partnerById(this.partnerId);
        },

        currencyShortCode() {
            if (!this.scoreRangePrediction || !this.winningTeamPrediction) {
                return '';
            }

            return this.getVoteTokenByCurrencyId(this.scoreRangePrediction.voteTokenCurrencyId).shortCode;
        },

        ledgerBalance() {
            if (!this.scoreRangePrediction || !this.winningTeamPrediction) {
                return 0;
            }

            const {
                balance = 0,
                lockedBalance = 0,
            } = this.decentralizedLedgerByToken(this.scoreRangePrediction.voteTokenCurrencyId);

            return (Number(balance) + Number(lockedBalance) || 0);
        },

        status() {
            if (!this.sportEvent || !this.sportEvent.userData || !this.sportEvent.userData.sportEventProgressionLabel) {
                return null;
            }

            return this.sportEvent.userData.sportEventProgressionLabel;
        },

        hasBalanceToVote() {
            const allowPredictionIfNotFan = this.getTeamAllowPredictionIfNotFan(this.teamId);

            if (allowPredictionIfNotFan) {
                return true;
            }

            return this.ledgerBalance > 0;
        },

        matchTime() {
            const date = (this.sportEvent.startAt || '').replace(' ', 'T');

            return `${format(date, 'weekdayLongDayShortMonthLong')} - ${format(date, 'time')}`;

        },

        userCanVote() {
            if (!this.scoreRangePrediction || !this.winningTeamPrediction) {
                return false;
            }

            return !this.userVotingEnded && this.hasBalanceToVote;
        },

        userVotingEnded() {
            return this.hasUserExistingScoreRangeVotingEnded(this.teamId);
        },

        showStatistics() {
            if (!this.scoreRangePrediction || !this.winningTeamPrediction) {
                return false;
            }

            return this.getUserScoreRangePredictionAnswer(this.teamId) || this.scoreRangePrediction.status === 'closed';
        },

        showPredictionOutcome() {
            return this.isSportEventResultDefined;
        },

        isInputDisabled() {
            return !this.userCanVote || this.isSubmitting || !this.scoreRangePrediction || !this.winningTeamPrediction;
        },

        userSubmittedAnswer() {
            if (!this.getUserScoreRangePredictionAnswer(this.teamId)) {
                if (this.userVotingEnded) {
                    return '-';
                }

                return '+';
            }

            return this.getUserScoreRangePredictionAnswer(this.teamId);
        },

        userChosenAnswer() {
            return this.userSubmittedAnswer;
        },

        userScoreRangePredictionAnswer() {
            return this.getUserScoreRangePredictionAnswer(this.teamId);
        },

        hasUserSelectedPredictions() {
            if (this.getUserSelectedIndex >= 0 && this.getUserSelectedTeamQualifier) {
                return true;
            }

            return false;
        },

        scoreRanges() {
            return AMERICAN_FOOTBALL_SCORE_RANGES;
        },

        outcomeCorrectScoreRange() {
            return (this.getOutcomeCorrectScoreRangeAmericanFootball(this.teamId) || {}).hasUserAchieved;
        },

        correctWinningTeam() {
            return (this.getOutcomeCorrectWinningTeamAmericanFootball(this.teamId) || {}).hasUserAchieved;
        },

        alternativeCrownLogo() {
            if (this.showPredictionOutcome && this.correctWinningTeam) {
                return 'green';
            }

            if (this.showPredictionOutcome && !this.correctWinningTeam) {
                return 'red';
            }

            if (this.showStatistics && !this.showPredictionOutcome) {
                return 'grey';
            }

            return '';
        },

        userChosenTeamQualifier() {
            if (!this.getUserWinningTeamPredictionAnswer(this.teamId)) {
                return '';
            }

            const selectedTeamId = this.getUserWinningTeamPredictionAnswer(this.teamId);

            if (selectedTeamId && this.homeTeam?.id === selectedTeamId) {
                return 'home';
            }

            if (selectedTeamId && this.awayTeam?.id === selectedTeamId) {
                return 'away';
            }

            return '';
        },
    },
    watch: {
        getLatestValidationError: 'handleError',
        getSubmittedPredictions: 'markVoteAsSubmitted',
        sportEvent: {
            deep: true,
            handler(sportEvent) {
                if (!sportEvent) {
                    this.$router.push('/404');
                }
            },
        },
    },
    async mounted() {
        const {
            sportEventId = null, teamId = null,
        } = this.$route.params;


        if (!this.wallet || !this.wallet.id) {
            this.getWallet({
                chilizCurrencyId: socios.chilizCurrencyId,
            });
        }

        await this.fetchTeamById(teamId);

        if (!sportEventId || !teamId || !this.partnerId) {
            this.$router.push('/404');
        }

        await this.fetchSportEvent({
            sportEventId,
            partnerId: this.partnerId,
        });

        this.fetchDefaultSportJerseys();

        const { scrollY } = this.$route.query;

        if (scrollY) {
            window.scrollTo(0, Number(scrollY));
        }
    },
    destroyed() {
        this.resetUserSelection();
    },
    methods: {
        ...mapActions({
            setModal: localEpicTypes.SET_MODAL,
            resetModal: localEpicTypes.RESET_MODAL,
            getWallet: sharedEpicTypes.GET_WALLET,
        }),

        ...mapActions('predictorSportEvents', [
            'fetchSportEvent',
            'postPrediction',
            'postAmericanFootballPrediction',
        ]),

        ...mapActions('predictorPredictableSportEvents', [
            'setUserSelectedIndex',
            'setUserSelectedTeamQualifier',
        ]),

        ...mapActions('predictorSports', ['fetchDefaultSportJerseys']),

        ...mapActions('predictorTeams', ['fetchTeamById']),

        openSelectorModal() {
            // If voting is not possible
            if (this.showStatistics || !this.hasBalanceToVote || this.userVotingEnded) {
                return;
            }

            this.setModal({
                componentToLoad: 'predictor-american-football-sheet',
                isVisible: true,
                isClosable: true,
                data: {
                    homeLogo: this.homeJersey,
                    awayLogo: this.awayJersey,
                    userChosenTeamQualifier: '',
                    alternativeCrownLogo: '',
                    homeTeam: this.homeTeam,
                    awayTeam: this.awayTeam,
                    scoreRangePredictionId: this.scoreRangePrediction?.id,
                    buttonCanBeDisabled: true,
                    scoreRanges: this.scoreRanges,
                },
                acceptButton: {
                    text: this.$t('predictors.details.submitPrediction'),
                    enabled: this.hasUserSelectedPredictions,
                    action: () => {
                        this.submitPrediction();
                        this.resetModal();
                    },
                },
            });
        },

        async submitPrediction() {
            this.isSubmitting = true;

            await this.postAmericanFootballPrediction([
                {
                    scoreRangePredictionId: this.scoreRangePrediction?.id,
                    scoreRangePredictionIdAnswer: this.scoreRanges[this.getUserSelectedIndex],
                    scoreRangePredictionPartnerId: this.partnerId,
                },
                {
                    winningTeamPredictionId: this.winningTeamPrediction.id,
                    winningTeamPredictionAnswer: this.getUserSelectedTeamQualifier === 'home' ? this.homeTeam.id : this.awayTeam.id,
                    winningTeamPredictionPartnerId: this.partnerId,
                },
            ]);

            this.isSubmitting = false;
        },

        openModal(submitSuccess) {
            const image = submitSuccess ? successIcon : errorIcon;

            const title = submitSuccess
                ? this.$t('predictors.details.predictionSubmittedTitleSuccess')
                : this.$t('predictors.details.predictionSubmittedTitleFail');

            const text = submitSuccess
                ? this.$t('predictors.details.predictionSubmittedTextSuccess')
                : this.$t('predictors.details.predictionSubmittedTextFailLineBreak');

            const buttonText = submitSuccess
                ? this.$t('global.button.continue')
                : this.$t('global.button.tryAgain');

            this.setModal({
                image,
                title,
                isVisible: true,
                messages: [
                    [
                        {
                            text,
                        },
                    ],
                ],
                acceptButton: {
                    text: buttonText,
                    action: () => {
                        if (submitSuccess) {
                            this.resetModal();

                            return;
                        }

                        this.openSelectorModal();
                    },
                },
            });
        },

        markVoteAsSubmitted(newVal) {
            if (newVal && newVal[this.sportEventId]) {
                this.isSubmitting = false;
                this.openModal(true);

                // Reset the store
                this.resetUserSelection();
            }
        },

        markVoteAsFailed() {
            this.isSubmitting = false;
            this.openModal(false);
        },

        handleError() {
            const isCurrentSportEvent = this.sportEvent && this.sportEventId === this.sportEvent.id;
            const brokenSportEvent = !this.sportEvent || !isCurrentSportEvent;

            if (!this.partner || (this.partner && !this.partner.id) || brokenSportEvent) {
                this.$router.push('/404');

                return;
            }

            this.markVoteAsFailed();
        },

        resetUserSelection() {
            this.setUserSelectedIndex(-1);
            this.setUserSelectedTeamQualifier('');
        },
    },
};
</script>

<style lang="scss" scoped>
.page {
    overflow-y: visible;
    min-height: calc(100vh - 6.5rem);
    background-color: $athens-gray-color;

    @include max-width(md) {
        &.predictor--partner-app {
            padding-top: 70px;

            .back-btn {
                top: 10rem;
            }
        }
    }
}

.banner {
    width: 100%;
    height: 22.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    background-size: cover;
    background-position-y: 50%;
    background-color: $dusty-gray-color;

    @include min-width(md) {
        height: 40rem;
    }

    &__loader {
        position: absolute;
        display: none;
    }

    .logo {
        width: 8.7rem;
        height: 8.7rem;
        align-self: initial;

        @include min-width(md) {
            width: 10rem;
            height: 10rem;
        }
    }

    &__partner-name {
        font-size: 3rem;
        font-family: "Mulish";
        font-weight: bold;
        color: $white-color;
    }
}

.center-wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    align-items: center;
    width: 100%;
    position: relative;
}

.center {
    box-sizing: border-box;
    margin: 0 1.7rem;

    @include min-width(xs) {
        width: calc(100% - 3.4rem);
    }

    @include min-width(sm) {
        max-width: 48rem;
    }

    @include min-width(md) {
        min-width: 48rem;
        max-width: calc(100% - 27rem);
    }

    @include min-width(xl) {
        max-width: 76rem;
    }
}

.no-prediction-spinner {
    align-self: center;
    height: 36rem;

    @include min-width(md) {
        height: 30rem;
    }
}

.prediction-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.prediction-carousel {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 1;
    margin: -4rem 0 7rem 0;
    width: 100%;

    @include min-width(lg) {
        width: 100%;
    }

    @include min-width(xl) {
        width: 80rem;
    }

    &__control {
        @include chevron($black-color, 3.25rem);

        display: none;
        position: absolute;
        top: 26rem;

        @include min-width(lg) {
            display: block;
            background-color: $white-color;
        }

        &--disabled {
            @include chevron($french-gray-color, 3.25rem);
        }
    }

    &__left-control {
        @include chevron-back();

        left: 4rem;

        @include min-width(xl) {
            left: -6rem;
        }
    }

    &__right-control {
        @include chevron-forward();

        right: 4rem;

        @include min-width(xl) {
            right: -6rem;
        }
    }
}

.prediction {
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 10px;
    background-color: $white-color;
    padding: 2.3rem 0.5rem 2.9rem 0.5rem;

    @include min-width(md) {
        padding: 3.5rem 1rem 3rem 1rem;
    }

    @include min-width(xl) {
        padding: 3.5rem 1rem 3rem 1rem;
    }

    &__status {
        margin-bottom: 1.6rem;
    }

    &__no-balance {
        margin: -1.3rem 0 1.6rem 0;
        color: $daintree-color;
        font-family: Nunito;
        font-size: 1.1rem;
        font-weight: bold;
        border: 1px solid rgba($dusty-gray-color, 0.2);
        border-radius: 16px;
        padding: 0.7rem 1.5rem;

        &:before {
            font-family: "icomoon";
            content: "\e929";
            font-size: 1rem;
            font-weight: bold;
            margin-right: 1.4rem;
        }
    }

    &__event-name {
        margin: 0 0 1rem 0;
        font-family: Nunito;
        font-size: 1.5rem;
        font-weight: bold;
        color: $daintree-color;

        @include min-width(md) {
            font-size: 2.5rem;
        }
    }

    &__match-time {
        margin-bottom: 0.5rem;
        font-family: Nunito;
        font-size: 1.2rem;
        color: $daintree-color;

        @include min-width(md) {
            font-size: 1.5rem;
        }
    }

    &__season-name {
        margin-bottom: 2rem;
        font-family: Nunito;
        font-size: 1.1rem;
        color: $hit-gray-color;

        @include min-width(md) {
            margin-bottom: 2.5rem;
            font-size: 1.5rem;
        }
    }

    &__versus {
        display: flex;
        align-items: center;

        &__dropdowns {
            display: flex;
            align-items: center;
        }

        &__logo {
            width: 8rem;
            height: 8rem;
        }

        &__wrapper {
            padding: 0 20px;
        }

        &__team {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 10rem;

            &-name {
                font-family: Nunito;
                font-style: normal;
                font-weight: 700;
                font-size: 1.3rem;
                line-height: 1.8rem;
                display: flex;
                align-items: center;
                text-align: center;
                color: $daintree-color;
            }
        }
    }

    &__vote-count {
        margin-top: 2rem;
        font-family: Nunito;
        font-size: 1.3rem;
        font-weight: bold;
        color: $daintree-color;

        @include min-width(md) {
            margin-top: 3rem;
            font-size: 1.5rem;
        }
    }

    &__submit {
        display: none;
        width: 17rem;
        height: 4rem;
        margin-top: 0.5rem;

        @include min-width(lg) {
            display: block;
        }
    }
}

.about {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    align-self: center;
    margin-bottom: 7.2rem;

    &__title {
        margin: 0 0 1rem 0;
        font-size: 2rem;
        font-weight: bold;
        font-family: Nunito;
        color: $daintree-color;

        @include min-width(md) {
            font-size: 2.5rem;
        }
    }

    &__description {
        font-family: Nunito;
        font-size: 1.3rem;
        white-space: pre-line;
        color: $daintree-color;

        @include min-width(md) {
            font-size: 1.5rem;
        }
    }
}

.popular {
    display: flex;
    flex-direction: column;
    align-self: center;
    margin-bottom: 7.2rem;

    &__title {
        margin: 0 0 1rem 0;
        font-size: 2rem;
        font-weight: bold;
        font-family: Nunito;
        color: $daintree-color;

        @include min-width(md) {
            font-size: 2.5rem;
        }
    }
}
</style>